import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  EMPTY_CHOICES,
  POLICY_VERSION,
  getOrCreateConsentId,
  postConsent,
  readStoredConsent,
  writeStoredConsent,
} from '../lib/consent';

// Persist "we've already logged an authenticated attribution for this
// browser session" across mounts / page reloads so the auto-attribution
// effect below can't create duplicate `page: 'login'` rows when:
//   1. StrictMode dev double-invokes effects on mount, or
//   2. React re-runs the [token, consentCtx] effect in App.tsx and calls
//      setAuthenticated with the same value repeatedly, or
//   3. The user simply hard-refreshes the page while already logged in.
// The stamp is cleared on real logout so the next login attributes fresh.
const AUTH_ATTRIBUTION_STAMP_KEY = 'ai_admin_last_auth_attribution';
const AUTH_ATTRIBUTION_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const hasRecentAuthAttribution = (): boolean => {
  try {
    const raw = localStorage.getItem(AUTH_ATTRIBUTION_STAMP_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < AUTH_ATTRIBUTION_TTL_MS;
  } catch {
    return false;
  }
};

const markAuthAttributed = (): void => {
  try {
    localStorage.setItem(AUTH_ATTRIBUTION_STAMP_KEY, String(Date.now()));
  } catch {
    // Storage may be unavailable — the in-memory ref still guards this
    // session; only cross-mount duplication is at risk.
  }
};

const clearAuthAttribution = (): void => {
  try {
    localStorage.removeItem(AUTH_ATTRIBUTION_STAMP_KEY);
  } catch {
    // No-op — see markAuthAttributed.
  }
};
import type {
  ConsentAction,
  ConsentPage,
  StoredConsent,
} from '../lib/consent';

// The admin console does NOT gate Sentry on user choice — see the header
// comment in `lib/consent.ts` for the rationale. Sentry is initialized
// unconditionally at app boot from `main.tsx`; the notice card here is a
// disclosure + acknowledgment, not a per-category consent gate.

interface ConsentContextValue {
  ready: boolean;
  /** True once the current-policy-version notice has been acknowledged. */
  hasAcknowledged: boolean;
  action: ConsentAction | null;
  /**
   * When true, the notice card is force-rendered (used by the sidebar
   * "Privacy notice" link to let a signed-in user re-view the disclosure).
   */
  isSettingsOpen: boolean;

  /** Record acknowledgment of the cookie / privacy notice. */
  acknowledge: (page: ConsentPage) => void;

  openSettings: () => void;
  closeSettings: () => void;

  /** True once axios has a bearer token. Determines POST endpoint. */
  setAuthenticated: (isAuthed: boolean) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
};

export const useConsentOptional = (): ConsentContextValue | null => useContext(ConsentContext);

interface ProviderProps {
  children: ReactNode;
}

export function ConsentProvider({ children }: ProviderProps) {
  const [ready, setReady] = useState(false);
  const [action, setAction] = useState<ConsentAction | null>(null);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  // Authenticated state is duplicated in a ref so the synchronous
  // `acknowledge` callback can read the current value without waiting for
  // a state-driven re-render — the state copy exists only to drive the
  // auth-attribution effect below.
  const [authenticated, setAuthenticatedState] = useState(false);
  const authenticatedRef = useRef<boolean>(false);
  // Guards the auto-attribution log so we only write one authenticated
  // consent-log row per login (regardless of how many times React
  // re-renders while auth is active). Reset on logout so the next login
  // fires a fresh attribution row.
  const authAttributionFiredRef = useRef<boolean>(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setAction(stored.action);
    }
    setReady(true);
  }, []);

  const acknowledge = useCallback((page: ConsentPage) => {
    const record: StoredConsent = {
      version: 1,
      policyVersion: POLICY_VERSION,
      choices: EMPTY_CHOICES,
      action: 'accept_all',
      updatedAt: new Date().toISOString(),
    };
    writeStoredConsent(record);
    setAction('accept_all');
    setSettingsOpen(false);

    const consentId = getOrCreateConsentId();
    // Fire-and-forget audit-log write. Sends `choices: {}` so the backend
    // row is well-formed even though there are no user-toggleable
    // categories on the admin surface.
    void postConsent({
      consentId,
      choices: EMPTY_CHOICES,
      action: 'accept_all',
      page,
      authenticated: authenticatedRef.current,
    });
    // If the user clicked "Got it" while already authenticated, the row we
    // just wrote is the authenticated attribution — no need for the
    // auto-attribution effect to duplicate it. Persist the stamp so a
    // subsequent page reload doesn't re-attribute either.
    if (authenticatedRef.current) {
      authAttributionFiredRef.current = true;
      markAuthAttributed();
    }
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const setAuthenticated = useCallback((isAuthed: boolean) => {
    // Only reset the auto-attribution guards on an ACTUAL logout transition
    // (previously authenticated → no longer authenticated). Idempotent
    // false→false calls during mount would otherwise clear the guard and
    // let a subsequent setAuthenticated(true) double-fire the effect,
    // producing duplicate `page: 'login'` rows.
    const wasAuthed = authenticatedRef.current;
    authenticatedRef.current = isAuthed;
    setAuthenticatedState(isAuthed);
    if (wasAuthed && !isAuthed) {
      authAttributionFiredRef.current = false;
      clearAuthAttribution();
    }
  }, []);

  // Auto-attribution: when the user logs in AND they already have a
  // stored acknowledgment on this browser (either from clicking "Got it"
  // on the login page or from a previous session on the same device),
  // write ONE authenticated `/api/consent` row so the DB record carries
  // their userId. Without this, only the pre-auth public row would exist,
  // and we would not know WHICH user acknowledged on this device.
  //
  // Guarded by two flags:
  //   authAttributionFiredRef  — in-memory per mount; blocks StrictMode
  //                              double-invoke and rapid effect refires.
  //   markAuthAttributed()     — localStorage stamp; blocks a fresh mount
  //                              on page reload from producing a duplicate
  //                              row for the same login session.
  useEffect(() => {
    if (!authenticated) return;
    if (action === null) return; // no acknowledgment on this device yet
    if (authAttributionFiredRef.current) return;
    if (hasRecentAuthAttribution()) {
      // Already logged an authenticated attribution for this browser in the
      // last 24h. Latch the in-memory guard so we don't re-check storage
      // every render, and skip the network write.
      authAttributionFiredRef.current = true;
      return;
    }
    authAttributionFiredRef.current = true;
    markAuthAttributed();

    const consentId = getOrCreateConsentId();
    void postConsent({
      consentId,
      choices: EMPTY_CHOICES,
      action: 'accept_all',
      page: 'login',
      authenticated: true,
    });
  }, [authenticated, action]);

  const value = useMemo<ConsentContextValue>(
    () => ({
      ready,
      hasAcknowledged: action !== null,
      action,
      isSettingsOpen,
      acknowledge,
      openSettings,
      closeSettings,
      setAuthenticated,
    }),
    [ready, action, isSettingsOpen, acknowledge, openSettings, closeSettings, setAuthenticated],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
